import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Button from "../../common/Button";
import Table from "../../common/CustomTable";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchPracticeByIdData, usePracticeEditSlice } from "../../../redux/slice/practice/practiceEditSlice";
import { updateUserInfo, useAuthSlice } from "../../../redux/slice/authSlice";
import CustomTextField from "../../common/CustomTextField";
import { Controller, useForm } from "react-hook-form";
import CustomNametField from "../../common/CustomNametField";
import CustomPatternTextField from "../../common/CustomPatternTextField";
import CustomRadioButton from "../../common/CustomRadioButton";
import { completePracticeRegistration, PracticeInfo } from "../../../redux/slice/practice/practiceRegistrationSlice";
import AuthorizedSignerPopup, { AuthorizedSignerInfo } from "../practice/AuthorizedSignerPopup";
import { PracticeLocationInfo } from "../../../redux/slice/practice/practiceLocationSlice";
import { PracticeProviderInfo } from "../../../redux/slice/practice/practiceProvidersSlice";
import PracticeLocationPopup, { initialState } from "../practice/PracticeLocationPopup";
import ProviderPopup, { providerInitialState } from "../practice/ProviderPopup";
import { Cancel, CheckCircleOutline, DeleteOutlineOutlined, LocationOn, ModeOutlined, PaymentOutlined, PersonOutline, VerifiedUserOutlined } from "@mui/icons-material";
import LegacyToolBar from "../../common/LegacyToolBar";
import { fullName, getCategoryDisplayLabel } from "../../../utils/Utility";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomNumberField from "../../common/CustomNumberField";
import { CodeTable, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { PRODUCT_CATEGORY, PRODUCT_CATEGORY_CODETABLE } from "../../../constants/applicationConstants"; // CS-1728 - Replacing static label to dynamic constant
import CustomCheckboxGroup from "../../common/CustomCheckboxGroup";
import { tokens } from "../../../theme";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { useModalStyles } from "../../common/useModalStyles";

interface CompletePracticeRegistrationModalProps {
  open: boolean;
  onClose: () => void;
}

interface Row {
  providerName: string;
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  npi: string;
  ptan: string;
}

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

const preferenceOptions = [
  {
    value: "dateOfShipment",
    label: "Date of Shipment"
  },
  {
    value: "dateOfDelivery",
    label: "Date of Delivery"
  }
];

const legacyItems = [
  { icon: <ModeOutlined color="info" />, text: "Edit" },
  { icon: <DeleteOutlineOutlined color="error" />, text: "Delete" },
];

const columnsPractice: Column[] = [
  { id: "streetAddress", label: "Street Address", minWidth: 200 },
  { id: "unitNumber", label: "Unit Number", minWidth: 100 },
  { id: "city", label: "City", minWidth: 100 },
  { id: "state", label: "State ", minWidth: 100 },
  { id: "zipCode", label: "Zip", minWidth: 100 },
  { id: "isBillingAddress", label: "Billing Address", minWidth: 100 },
  { id: "action", label: "Action", minWidth: 100 },
];

const columnsProvider: Column[] = [
  { id: "providerName", label: "Provider Name", minWidth: 200 },
  { id: "npi", label: "Provider NPI", minWidth: 100 },
  { id: "ptan", label: "Provider PTAN", minWidth: 100 },
  { id: "taxId", label: "Provider Tax ID", minWidth: 100 },
  { id: "email", label: "Provider Email", minWidth: 200 },
  { id: "action", label: "Action", minWidth: 100 },
];

const columnsSigner: Column[] = [
  { id: "fullTitleName", label: "Full Name", minWidth: 200 },
  { id: "email", label: "Email", minWidth: 100 },
  { id: "isOwner", label: "Ownership", minWidth: 100 },
  { id: "position", label: "Position", minWidth: 100 },
  { id: "action", label: "Action", minWidth: 100 },
];

const CARE_AREA_DESCRIPTIONS: Record<string, string> = {
  [PRODUCT_CATEGORY.ADVANCED]: "Skin substitutes, cellular therapies, PRP/platelet-based products",
  [PRODUCT_CATEGORY.CONSERVATIVE]: "Collagen dressings, advanced wound care supplies",
  [PRODUCT_CATEGORY.STANDARD]: "Traditional wound care, compression, negative pressure",
  [PRODUCT_CATEGORY.GENERAL]: "GLP-1s, wellness, and other non-wound care products",
};

const CompletePracticeRegistrationModal = ({
  open,
  onClose,
}: CompletePracticeRegistrationModalProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [billerType, setBillerType] = useState<"primary" | "accountPayable" | "different">("different");
  const [billingType, setBillingType] = useState<"individual" | "group">("individual",);
  const [dmeLicense, setDmeLicense] = useState<String | null>(null);
  const [specializations, setSpecializations] = useState<
    Record<string, boolean>
  >({
    Podiatry: false,
    "Wound Care": false,
    Dermatology: false,
    Vascular: false,
    Orthopedics: false,
    "General Surgery": false,
  });
  const dispatch = useAppDispatch();
  const { userInfo } = useAuthSlice();
  const { practiceEditInfo } = usePracticeEditSlice();
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState,
    watch,
    unregister,
    clearErrors,
    trigger
  } = useForm<PracticeInfo>();

  const [billingCollagen, setBillingCollagen] = useState<String | null>(null);
  const [billMedicare, setBillMedicare] = useState<String | null>(null);
  const [billMedicareAdvantage, setBillMedicareAdvantage] = useState<String | null>(null);
  const [showPracticesPopup, setShowPracticesPopup] = useState(false);
  const [showProviderPopup, setShowProviderPopup] = useState(false);
  const [showAuthorizedSignerPopup, setShowAuthorizedSignerPopup] = useState(false);
  const [authorizedSignerData, setAuthorizedSignerData] = useState<AuthorizedSignerInfo>();
  const [locationItemObject, setLocationItemObject] = useState<PracticeLocationInfo>(initialState);
  const [providerItemObject, setProviderItemObject] = useState<PracticeProviderInfo>(providerInitialState);
  const [locationIndex, setLocationIndex] = useState<number | undefined>();
  const [providerIndex, setProviderIndex] = useState<number | undefined>();
  const [showBillingAddressOption, setShowBillingAddressOption] = useState(false);
  const colors = tokens(theme.palette.mode);

  const watchedLocations = watch("practiceLocations");
  const hasPracticeLocations = watchedLocations?.length > 0;
  const watchedProviders = watch("practiceProviders");
  const hasPracticeProviders = watchedProviders?.length > 0;
  const watchedAuthorizedSigners = watch("practiceRequestInfo.authorizedSigners");
  const hasAuthorizedSigners = watchedAuthorizedSigners?.length > 0;

  const [provider, setProvider] = useState<PracticeProviderInfo[]>([]);
  const [officeContactChecked, setOfficeContactChecked] = useState<boolean>(false);
  const [noRepChecked, setNoRepChecked] = useState<boolean>(false);
  const [accountInformationChecked, setAccountInformationChecked] = useState<boolean>(false);

  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();

  const [practiceSpecializationList, setPracticeSpecializationList] = useState<CodeTable[]>([]);
  const [woundCareOptions, setWoundCareOptions] = useState<CodeTable[]>([]);
  const [placeOfService, setPlaceOfService] = useState<CodeTable[]>([]);
  const [insurancePayerList, setInsurancePayerList] = useState<CodeTable[]>([]);
  const [productCategories, setProductCategories] = useState<CodeTable[]>([]);
  const {primaryBtnSx, cancelBtnSx} = useModalStyles();
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const productCategoryOptions = useMemo(() => {
    return productCategories.map(item => ({
      label: item.displayValue,
      value: item.keyName,
      description: CARE_AREA_DESCRIPTIONS[item.keyName] || "",
    }));
  }, [productCategories]);

  const watchedCategories = watch("practiceRequestInfo.interestedProductCategory") || [];
  const allCareAreasSelected =
    productCategoryOptions.length > 0 &&
    productCategoryOptions.every((o) => watchedCategories.includes(o.value));

  const mapDisplayValueToKeyName = (displayValue: string): string => {
    const category = productCategories.find(item => item.displayValue === displayValue);
    return category?.keyName || displayValue;
  };

  const handleProductCategoryChange = () => {
    const selected = getValues("practiceRequestInfo.interestedProductCategory");
    // CS-1760 - Compare against raw keynames
    if ((selected || []).includes(PRODUCT_CATEGORY.CONSERVATIVE)) {
      setValue("dosPreference", "dateOfShipment");
    } else {
      setValue("dosPreference", "");
    }
    trigger("practiceRequestInfo.interestedProductCategory");
  }

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark
        ? "rgba(255,255,255,0.03)"
        : "rgba(0,0,0,0.02)",
      borderRadius: 1.5,
      fontSize: "0.88rem",
      "& fieldset": {
        borderColor: isDark
          ? "rgba(255,255,255,0.1)"
          : "rgba(0,0,0,0.15)",
      },
      "&:hover fieldset": { borderColor: "#14b8a6" },
      "&.Mui-focused fieldset": {
        borderColor: "#14b8a6",
        borderWidth: 1,
      },
      "& input": {
        color: isDark ? "#e2e8f0" : "#1e293b",
        py: 1.2,
        px: 1.5,
        "&::placeholder": {
          color: isDark ? "#475569" : "#94a3b8",
          opacity: 1,
        },
      },
    },
  };

  const labelSx = {
    fontSize: "0.82rem",
    fontWeight: 600,
    color: isDark ? "#94a3b8" : "#64748b",
    mb: 0.6,
    display: "block",
  };

  const sectionTitleSx = {
    fontSize: "1rem",
    fontWeight: 700,
    color: isDark ? "#e2e8f0" : "#1e293b",
    mb: 1,
  };

  const radioSx = {
    color: isDark ? "#64748b" : "#94a3b8",
    "&.Mui-checked": { color: "#14b8a6" },
    padding: "4px 8px",
  };

  const checkboxSx = {
    color: isDark ? "#64748b" : "#94a3b8",
    "&.Mui-checked": { color: "#14b8a6" },
    padding: "4px 8px",
  };

  const dividerSx = {
    mb: 2.5,
    borderColor: isDark
      ? "rgba(255,255,255,0.07)"
      : "rgba(0,0,0,0.07)",
  };

  const addBtnSx = {
    textTransform: "none",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "#14b8a6",
    backgroundColor: "transparent",
    border: "1.5px solid rgba(20,184,166,0.5)",
    borderRadius: 2,
    px: 1.8,
    py: 0.5,
    "&:hover": {
      backgroundColor: "transparent",
      borderColor: "#14b8a6",
    },
  };

  const radioLabelSx = {
    fontSize: "0.85rem",
    color: isDark ? "#cbd5e1" : "#475569",
  };

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      setWoundCareOptions(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "WoundCare")
      );
      const sortedStateList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "PlaceOfService"),
      ].sort((a, b) => {
        const matchA = a.displayValue.match(/\((\d+)/);
        const numA: number | null = matchA ? parseInt(matchA[1], 10) : null;

        const matchB = b.displayValue.match(/\((\d+)/);
        const numB: number | null = matchB ? parseInt(matchB[1], 10) : null;

        if (numA === null && numB === null) return 0;
        if (numA === null) return 1;
        if (numB === null) return -1;
        return numA - numB;
      });

      setPlaceOfService(sortedStateList);
      setInsurancePayerList(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "InsurancePayer")
      );

      setPracticeSpecializationList(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "PracticeSpecializations")
      );

      const sortedProductCategoryList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, PRODUCT_CATEGORY_CODETABLE),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setProductCategories(sortedProductCategoryList);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  // Update practice location
  const updatePracticeLocation = (
    index: number,
    updatedLocation: PracticeLocationInfo
  ) => {
    let updatedLocations = getValues("practiceLocations");
    updatedLocations[index] = updatedLocation;
    setValue("practiceLocations", updatedLocations);
  };

  // Update practice provider
  const updatePracticeProvider = (
    index: number,
    updatedProvider: PracticeProviderInfo
  ) => {
    let updatedProviders = getValues("practiceProviders");
    updatedProviders[index] = updatedProvider;
    setValue("practiceProviders", updatedProviders);
  };

  const addPractice = (practiceLocation: PracticeLocationInfo) => {
    let practiceLocations = getValues("practiceLocations") || [];

    if (practiceLocation.isBillingAddress === "yes") {
      const hasBillingAddress = practiceLocations.some(
        (location) => location.isBillingAddress === "yes"
      );
    }

    if (locationIndex !== undefined) {
      const oldLocation = practiceLocations[locationIndex];
      const updatedLocation = { ...oldLocation, ...practiceLocation };
      updatePracticeLocation(locationIndex, updatedLocation);
    } else {
      practiceLocations = [...practiceLocations, practiceLocation];
      setValue("practiceLocations", practiceLocations);
    }
    setShowBillingAddressOption(
      practiceLocations.length > 0 &&
      !practiceLocations.some(
        (location) => location.isBillingAddress === "yes"
      )
    );
  };

  const handleProviderChange = (name: string, value: string) => {
    setProvider((prevPro) => ({
      ...prevPro,
      [name]: value,
    }));
  };

  const addSigner = (signer: AuthorizedSignerInfo) => {
    let authorizedSigners = getValues("practiceRequestInfo.authorizedSigners");
    console.log(signer.index)
    if (signer.index || signer.index === 0) {
      authorizedSigners[signer.index] = signer;
      setValue("practiceRequestInfo.authorizedSigners", authorizedSigners);
    }
    else if (authorizedSigners)
      setValue("practiceRequestInfo.authorizedSigners", [
        ...getValues("practiceRequestInfo.authorizedSigners"),
        signer,
      ]);
    else setValue("practiceRequestInfo.authorizedSigners", [signer]);
  };

  const addProvider = (provider: PracticeProviderInfo) => {
    let practiceProviders = getValues("practiceProviders");
    if (providerIndex !== undefined) {
      const oldProvider = practiceProviders[providerIndex];
      const updatedProvider = { ...oldProvider, ...provider };
      updatePracticeProvider(providerIndex, updatedProvider);
    } else {
      if (practiceProviders)
        setValue("practiceProviders", [
          ...getValues("practiceProviders"),
          provider,
        ]);
      else setValue("practiceProviders", [provider]);
    }
  };

  const handleChange = (name: string, value: any) => {
    switch (name) {
      case "dmeLicense":
        setDmeLicense(value);
        setValue("practiceRequestInfo.dmeLicense", value);
        if (value !== "true") {
          setBillingCollagen(null);
          setBillMedicare(null);
          setBillMedicareAdvantage(null);
          setValue("practiceRequestInfo.billingCollagen", null);
          setValue("practiceRequestInfo.billMedicare", null);
          setValue("practiceRequestInfo.billMedicareAdvantage", null);
          setValue("practiceRequestInfo.billCommercial", null);
        }
        break;
      case "billingCollagen":
        setBillingCollagen(value);
        setValue("practiceRequestInfo.billingCollagen", value);
        if (value !== "true") {
          setBillMedicare(null);
          setBillMedicareAdvantage(null);
          setValue("practiceRequestInfo.billMedicare", null);
          setValue("practiceRequestInfo.billMedicareAdvantage", null);
          setValue("practiceRequestInfo.billCommercial", null);
        }
        break;
      case "billMedicare":
        setBillMedicare(value);
        setValue("practiceRequestInfo.billMedicare", value);
        // if (value !== "true") {
        //   setBillMedicareAdvantage(null);
        //   setValue("practiceRequestInfo.billMedicareAdvantage", null);
        //   setValue("practiceRequestInfo.billCommercial", null);

        // }
        break;
      case "billMedicareAdvantage":
        setBillMedicareAdvantage(value);
        setValue("practiceRequestInfo.billMedicareAdvantage", value);
        // if (value !== "true") {
        //   setValue("practiceRequestInfo.billCommercial", null);
        // }
        break;
      default:
        break;
    }
  };

  const clickShowPracticesPopup = () => {
    setShowPracticesPopup(true);
    if (
      getValues("practiceLocations") === undefined ||
      getValues("practiceLocations").length == 0 ||
      locationIndex == 0
    ) {
      setShowBillingAddressOption(true);
    }
  };

  const clickShowAuthorizedSignerPopup = () => {
    setShowAuthorizedSignerPopup(true);
  };

  const clickCloseAuthorizedSignerPopup = () => {
    setShowAuthorizedSignerPopup(false);
    setAuthorizedSignerData(undefined)

  };

  const clickClosePracticesPopup = () => {
    setShowPracticesPopup(false);
    setLocationItemObject(initialState);
    setLocationIndex(undefined);
    setShowBillingAddressOption(false);
  };

  const clickShowProviderPopup = () => {
    setShowProviderPopup(true);
  };

  const clickCloseProviderPopup = () => {
    setShowProviderPopup(false);
    setProviderItemObject(providerInitialState);
    setProviderIndex(undefined);
  };

  const transformPracticeLocations = (
    locations: { isBillingAddress: any }[]
  ) => {
    return locations.map((location: { isBillingAddress: any }) => ({
      ...location,
      isBillingAddress:
        location.isBillingAddress?.toLowerCase() === "yes" ? "Yes" : "No",
    }));
  };

  const formatAuthorizedSigners = (signers: AuthorizedSignerInfo[] | undefined) => {
    if (!signers) return [];
    return signers.map((signer) => ({
      ...signer,
      fullTitleName: signer.title ? `${signer.title} ${signer.fullName}` : signer.fullName
    }));
  };

  const handleEditActionLocation = (row: any, rowIndex: number) => {
    if (rowIndex === 0) {
      setShowBillingAddressOption(true);
    } else {
      setShowBillingAddressOption(false);
    }
    const editedRow = {
      ...row,
      isBillingAddress: row.isBillingAddress === "Yes" ? "yes" : "no",
    };
    setLocationIndex(rowIndex);
    setLocationItemObject(editedRow);
    clickShowPracticesPopup();
  };

  const handleDeleteActionLocation = (row: any, rowIndex: number) => {
    let addedPracticeLocations = getValues("practiceLocations");
    if (rowIndex >= 0 && rowIndex < addedPracticeLocations.length) {
      addedPracticeLocations.splice(rowIndex, 1);
      setValue("practiceLocations", addedPracticeLocations);
    } else {
      console.error("Invalid index");
    }
    if (getValues("practiceLocations").length === 0) {
      setShowBillingAddressOption(true);
    }
    setLocationIndex(rowIndex);
  };

  const handleEditActionProvider = (row: any, rowIndex: number) => {
    setProviderIndex(rowIndex);
    setProviderItemObject(row);
    clickShowProviderPopup();
  };

  const handleDeleteActionProvider = (row: any, rowIndex: number) => {
    let addedPracticeProviders = formattedProvidersInfo;
    if (rowIndex >= 0 && rowIndex < addedPracticeProviders.length) {
      addedPracticeProviders.splice(rowIndex, 1);
      // @ts-ignore
      setValue("practiceProviders", addedPracticeProviders);
    } else {
      console.error("Invalid index");
    }
  };

  const handleRowClick = (row: any) => { };

  useEffect(() => {

    const contactFirstName = getValues("contactFirstName")
    const contactLastName = getValues("contactLastName")
    const contactMiddleName = getValues("contactMiddleName")
    const contactPhone = getValues("contactPhone")
    const contactEMail = getValues("contactEMail")

    if (officeContactChecked) {
      setValue("practiceRequestInfo.billerFirstName", contactFirstName)
      setValue("practiceRequestInfo.billerMiddleName", contactMiddleName)
      setValue("practiceRequestInfo.billerLastName", contactLastName)
      setValue("practiceRequestInfo.billerContactPhone", contactPhone)
      setValue("practiceRequestInfo.billerContactEmail", contactEMail)
    } else {
      setValue("practiceRequestInfo.billerFirstName", "")
      setValue("practiceRequestInfo.billerMiddleName", "")
      setValue("practiceRequestInfo.billerLastName", "")
      setValue("practiceRequestInfo.billerContactPhone", "")
      setValue("practiceRequestInfo.billerContactEmail", "")
    }
  }, [officeContactChecked])
  useEffect(() => {

    const apFirstName = getValues("apFirstName")
    const apLastName = getValues("apLastName")
    const apMiddleName = getValues("apMiddleName")
    const apPhone = getValues("apPhone")
    const apEmail = getValues("apEmail")

    if (accountInformationChecked) {
      setValue("practiceRequestInfo.billerFirstName", apFirstName)
      setValue("practiceRequestInfo.billerMiddleName", apMiddleName)
      setValue("practiceRequestInfo.billerLastName", apLastName)
      setValue("practiceRequestInfo.billerContactPhone", apPhone)
      setValue("practiceRequestInfo.billerContactEmail", apEmail)
    } else {
      setValue("practiceRequestInfo.billerFirstName", "")
      setValue("practiceRequestInfo.billerMiddleName", "")
      setValue("practiceRequestInfo.billerLastName", "")
      setValue("practiceRequestInfo.billerContactPhone", "")
      setValue("practiceRequestInfo.billerContactEmail", "")
    }
  }, [accountInformationChecked])

  /* istanbul ignore next -- never-used declaration: only call site is commented-out JSX */
  const actionComponentLocationPopup = (
    row: any,
    rowIndex: number
  ): JSX.Element => {
    return (
      <>
        <Button
          color="info"
          size="small"
          variant="outlined"
          tooltipMessage="Edit"
          tooltipPlacement="top"
          onClick={() => handleEditActionLocation(row, rowIndex)}
        >
          <ModeOutlined fontSize="small" />
        </Button>{" "}
        <Button
          color="error"
          size="small"
          variant="outlined"
          tooltipMessage="Delete"
          tooltipPlacement="top"
          onClick={() => handleDeleteActionLocation(row, rowIndex)}
        >
          <DeleteOutlineOutlined fontSize="small" />
        </Button>
      </>
    );
  };

  /* istanbul ignore next -- never-used declaration: only call site is commented-out JSX */
  const actionComponentProviderPopup = (
    row: any,
    rowIndex: number
  ): JSX.Element => {
    return (
      <>
        <Button
          color="info"
          size="small"
          variant="outlined"
          tooltipMessage="Edit"
          tooltipPlacement="top"
          onClick={() => handleEditActionProvider(row, rowIndex)}
        >
          <ModeOutlined fontSize="small" />
        </Button>{" "}
        <Button
          color="error"
          size="small"
          variant="outlined"
          tooltipMessage="Delete"
          tooltipPlacement="top"
          onClick={() => handleDeleteActionProvider(row, rowIndex)}
        >
          <DeleteOutlineOutlined fontSize="small" />
        </Button>
      </>
    );
  };

  /* istanbul ignore next -- never-used declaration: only call site is commented-out JSX */
  const actionComponentAuthorizedSigner = (row: AuthorizedSignerInfo, rowIndex: number): JSX.Element => {
    return (
      <>
        <Button
          color="info"
          size="small"
          variant="outlined"
          onClick={() => handleSignerChange(row, rowIndex)}
          tooltipMessage="Edit"
          tooltipPlacement="top"
        >
          <ModeOutlined fontSize="small" />
        </Button>{" "}
        <Button
          color="error"
          size="small"
          variant="outlined"
          onClick={() => handleDeleteAuthorizedSigner(row, rowIndex)}
          tooltipMessage="Delete"
          tooltipPlacement="top"
        >
          <DeleteOutlineOutlined fontSize="small" />
        </Button>
      </>
    )
  }

  const handleSignerChange = (row: AuthorizedSignerInfo, rowIndex: number) => {
    let obj: AuthorizedSignerInfo = {
      ...row,
      index: rowIndex,
    }
    setShowAuthorizedSignerPopup(true);
    setAuthorizedSignerData(obj);
  };

  const handleDeleteAuthorizedSigner = (row: AuthorizedSignerInfo, rowIndex: number) => {
    let addedAuthorizedSigners = getValues("practiceRequestInfo.authorizedSigners");
    if (rowIndex >= 0 && rowIndex < addedAuthorizedSigners.length) {
      addedAuthorizedSigners.splice(rowIndex, 1);
      setValue("practiceRequestInfo.authorizedSigners", addedAuthorizedSigners);
    }
  }

  const getErrorAuthorizedSignerMessage = (field: any, fieldState: any, hasAuthorizedSigners: any) => {

    if (!hasAuthorizedSigners) {
      return "At least 1 Authorized Signer is required.";
    } else {
      return "";
    }
  }

  const getErrorLocationMessage = (field: any, fieldState: any, hasPracticeLocations: any) => {

    if (!hasPracticeLocations) {
      return "At least 1 practice location is required.";
    } else {
      let practiceLocations = getValues("practiceLocations");
      const hasBillingAddress = practiceLocations.some(
        (location) => location.isBillingAddress === "yes"
      );
      if (hasBillingAddress) {
        return "";
      } else {
        return "At least one practice location must be marked as a billing address.";
      }
    }
  }

  const validatePracticeLocations = () => {
    let practiceLocations = getValues("practiceLocations");
    const hasBillingAddress = practiceLocations.some(
      (location) => location.isBillingAddress === "yes"
    );
    if (hasBillingAddress) {
      return true;
    } else {
      return "At least one practice location must be marked as a billing address.";
    }
  };

  const formattedProvidersInfo: Row[] = getValues("practiceProviders")
    ? getValues("practiceProviders").map((practice: any) => ({
      providerName: fullName(
        practice.title,
        practice.firstName,
        practice.middleName,
        practice.lastName
      ),
      title: practice.title,
      firstName: practice.firstName,
      middleName: practice.middleName,
      lastName: practice.lastName,
      npi: practice.npi,
      ptan: practice.ptan,
      taxId: practice.taxId,
      email: practice.email,
    }))
    : [];

  useEffect(() => {
    const firstId = userInfo?.entityIds?.[0];
    if (firstId != null) {
      dispatch(fetchPracticeByIdData(firstId.toString()));
    }
  }, []);

  useEffect(() => {
    if (!practiceEditInfo) return;
    reset(practiceEditInfo);
  }, [practiceEditInfo]);

  const onError = (_errors: any, e: any) => {
    const dialogContent = dialogContentRef.current;
    if (!dialogContent) return;

    const firstErrorEl = dialogContent.querySelector(
      'p.Mui-error, [aria-invalid="true"], .MuiFormHelperText-root.Mui-error'
    );

    if (firstErrorEl) {
      firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      dialogContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const onSubmit = async (data: PracticeInfo) => {
    const response = await dispatch(completePracticeRegistration({ practiceData: data, interceptorOption: { skipErrorPopup: true } }));
    if (response.payload.code === "V3OK" && response.payload.data === "SUCCESS") {
      let errorObj: SingleAlertInfo = {
        message: "Practice Registration Submitted Successfully.",
        alertType: "success",
      };
      dispatch(setSingleAlertObj(errorObj));
      dispatch(updateUserInfo({ associatedPracticeStatus: "SUBMITTED" }));
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: isDark ? "#14171f" : "#f8fafc",
          backgroundImage: "none",
          borderRadius: isMobile ? 0 : 3,
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
            }`,
          boxShadow: "0 25px 80px rgba(0,0,0,0.5)",
          maxHeight: "92vh",
        },
      }}
    >
      {/* ── Title ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"
            }`,
        }}
      >
        <Typography
          fontWeight={700}
          sx={{ fontSize: "1.15rem", color: isDark ? "#e2e8f0" : "#1e293b" }}
        >
          Complete Practice Registration
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: isDark ? "#64748b" : "#94a3b8",
            "&:hover": { color: "#e2e8f0" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit, onError)}
       style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {/* ── Content ── */}
        <DialogContent ref={dialogContentRef} sx={{ px: 3, py: 3, overflowY: "auto", flex: 1,minHeight: 0 }}>

          {/* ── Practice Information ── */}
          <Typography sx={sectionTitleSx}>Practice Information</Typography>
          <Divider sx={dividerSx} />

          <Grid container spacing={2.5} mb={2}>
            <Grid item xs={12} sm={6}>
              <Typography component="label" sx={labelSx}>Practice Name *</Typography>
              <CustomTextField control={control} controllerName="practiceName"
                placeholder="Enter practice name"
                size="small"
                rules={{
                  required: "Practice Name is required.",
                  validate: {
                    customValidation: (value: string) => {
                      if (value?.trim() === "") {
                        return "Practice Name is required.";
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" sx={labelSx}>Practice Tax ID *</Typography>
              <CustomTextField control={control} controllerName="taxId" placeholder="XX-XXXXXXX" maxLength={10}
                size="small"
                rules={{
                  required: "Practice Tax ID is required.",
                  validate: {
                    doesNotContainSpaces: (value: string) =>
                      !/\s/.test(value) || `Tax ID cannot contain spaces.`,
                    customValidation: (value: string) => {
                      if (value?.trim() === "") {
                        return "Practice Tax ID is required.";
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" sx={labelSx}>Practice NPI *</Typography>
              <CustomTextField control={control} controllerName="npi" placeholder="10-digit NPI" maxLength={10}
                size="small"
                rules={{
                  required: "NPI is required.",
                  validate: {
                    doesNotContainSpaces: (value: string) =>
                      !/\s/.test(value) || `NPI cannot contain spaces.`,
                    customValidation: (value: string) => {
                      if (value?.trim() === "") {
                        return "NPI is required.";
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" sx={labelSx}>Practice PTAN *</Typography>
              <CustomTextField control={control} controllerName="ptan" placeholder="Enter PTAN" maxLength={10}
                size="small"
                rules={{
                  required: "PTAN is required.",
                  validate: {
                    doesNotContainSpaces: (value: string) =>
                      !/\s/.test(value) || `PTAN cannot contain spaces.`,
                    customValidation: (value: string) => {
                      if (value?.trim() === "") {
                        return "PTAN is required.";
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography component="label" sx={labelSx}>Contact Name *</Typography>
              <CustomNametField control={control} context="Contact" size="small" xs={12} md={4} isRequired={true} usePlaceholder={true} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography component="label" sx={labelSx}>Contact Email *</Typography>
              <CustomTextField control={control} controllerName="contactEmail" placeholder="email@practice.com"
                size="small"
                rules={{
                  required: "Contact Email is required.",
                  pattern: {
                    value: /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|n\/a|N\/A)$/i,
                    message: "Invalid email.",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography component="label" sx={labelSx}>Contact Phone *</Typography>
              <CustomPatternTextField control={control} placeholder="(555) 555-5555" name="contactPhone"
                size="small"
                rules={{
                  required: "Contact Phone is required.",
                  pattern: {
                    value: /^(\+\d{1,2}\s)?\(\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                    message: "Invalid phone number.",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography component="label" sx={labelSx}>Contact Fax</Typography>
              <CustomPatternTextField control={control} placeholder="(555) 555-5555" name="contactFax"
                size="small"
                rules={{
                  pattern: {
                    value: /^(\+\d{1,2}\s)?\(\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                    message: "Invalid fax number.",
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* How do you bill */}
          <Box mb={1.5}>
            <Typography component="label" sx={labelSx}>How do you bill? *</Typography>
            <Grid item>
              <CustomRadioButton control={control} controllerName="billingType"
                radioSx={radioSx}
                radioLabelSx={radioLabelSx}
                options={[
                  { value: "INDIVIDUAL", label: "Individual NPI" },
                  { value: "GROUP", label: "Group NPI" },
                ]}
                rules={{ required: "Billing type is required." }}
              />
            </Grid>
          </Box>

          {/* DME License */}
          <Box mb={3}>
            <Typography component="label" sx={labelSx}>
              Do you have a DME license? *
            </Typography>
            <CustomRadioButton
              control={control}
              controllerName="practiceRequestInfo.dmeLicense"
              radioSx={radioSx}
              radioLabelSx={radioLabelSx}
              options={[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ]}
              rules={{ required: "Please select the option" }}
              onChange={(e) => handleChange("dmeLicense", e.target.value)}
            />

            {dmeLicense === "true" && (
              <Grid item xs={12} sm={4}>
                <Typography component="label" sx={labelSx}>
                  Will you be doing your own DME billing? *
                </Typography>
                <CustomRadioButton
                  control={control}
                  controllerName="practiceRequestInfo.billingCollagen"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                  rules={{ required: "Please select the option" }}
                  onChange={(e) =>
                    handleChange("billingCollagen", e.target.value)
                  }
                  radioSx={radioSx}
                  radioLabelSx={radioLabelSx}
                />
              </Grid>
            )}
            {dmeLicense === "true" && billingCollagen === "true" && (
              <Grid item xs={12} sm={4}>
                <Typography component="label" sx={labelSx}>
                  Will you bill Medicare?  *
                </Typography>
                <CustomRadioButton
                  control={control}
                  controllerName="practiceRequestInfo.billMedicare"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                  rules={{ required: "Please select the option" }}
                  onChange={(e) =>
                    handleChange("billMedicare", e.target.value)
                  }
                  radioSx={radioSx}
                  radioLabelSx={radioLabelSx}
                />
              </Grid>
            )}
            {dmeLicense === "true" && billingCollagen === "true" && (
              <Grid item xs={12} sm={4}>
                <Typography component="label" sx={labelSx}>
                  Will you bill Medicare Advantage? *
                </Typography>
                <CustomRadioButton
                  control={control}
                  controllerName="practiceRequestInfo.billMedicareAdvantage"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                  rules={{ required: "Please select the option" }}
                  onChange={(e) =>
                    handleChange("billMedicareAdvantage", e.target.value)
                  }
                  radioSx={radioSx}
                  radioLabelSx={radioLabelSx}
                />
              </Grid>
            )}
            {dmeLicense === "true" && billingCollagen === "true" && (
              <Grid item xs={12} sm={4}>
                <Typography component="label" sx={labelSx}>
                  Will you bill Commercial? *
                </Typography>
                <CustomRadioButton
                  control={control}
                  controllerName="practiceRequestInfo.billCommercial"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                  rules={{ required: "Please select the option" }}
                  radioSx={radioSx}
                  radioLabelSx={radioLabelSx}
                />
              </Grid>
            )}

          </Box>

          {/* ── Practice Locations ── */}
          {/* <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography sx={sectionTitleSx}>Practice Locations *</Typography>
            <Button startIcon={<AddIcon />} size="small" sx={addBtnSx} onClick={clickShowPracticesPopup}>
              Add Location
            </Button>
          </Box>
          <Divider sx={dividerSx} />
          {hasPracticeLocations && (
            <Box pt={1}>
              <LegacyToolBar items={legacyItems} />
              <Table
                columns={columnsPractice}
                rows={transformPracticeLocations(
                  getValues("practiceLocations")
                )}
                onRowClick={handleRowClick}
                actionComponent={(row: any, rowIndex: number) =>
                  actionComponentLocationPopup(row, rowIndex)
                }
                {...PracticeLocationPopup}
                disablePagination={true}
                borderBox={true}
              />
            </Box>
          )}
          {!hasPracticeLocations && (
            <Typography
              sx={{ fontSize: "0.85rem", color: isDark ? "#64748b" : "#94a3b8", mb: 3 }}
            >
              No practice locations added yet.
            </Typography>
          )} */}

          <Controller
            name="practiceLocations"
            control={control}
            rules={{
              required: "At least 1 practice location is required.",
              validate: { validatePracticeLocations },
            }}
            render={({ field, fieldState }) => {
              const hasPracticeLocations = getValues("practiceLocations")?.length > 0;
              return (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={sectionTitleSx}>Practice Locations *</Typography>
                    <Button
                      startIcon={<AddIcon />}
                      size="small"
                      sx={addBtnSx}
                      onClick={clickShowPracticesPopup}
                    >
                      Add Location
                    </Button>
                  </Box>
                  <Divider sx={dividerSx} />

                  {fieldState.error && (
                    <Typography
                      color="error"
                      variant="body2"
                      style={{ marginTop: "8px" }}
                    >
                      {getErrorLocationMessage(field, fieldState, hasPracticeLocations)}
                    </Typography>
                  )}

                  {hasPracticeLocations ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                      {transformPracticeLocations(getValues("practiceLocations")).map((location: any, rowIndex: number) => {
                        const addressLine = [location.city, location.state, location.zipCode]
                          .filter(Boolean)
                          .join(", ");
                        const isBilling = location.isBillingAddress === "Yes";
                        return (
                          <Box
                            key={rowIndex}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: 2,
                              py: 1.5,
                              borderRadius: 2,
                              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                              backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                              transition: "border-color 0.15s",
                              "&:hover": {
                                borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)",
                              },
                            }}
                          >
                            {/* Left: icon + text */}
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                              <LocationOn
                                sx={{
                                  mt: 0.2,
                                  fontSize: "1.2rem",
                                  color: isDark ? "#475569" : "#94a3b8",
                                  flexShrink: 0,
                                }}
                              />
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "0.88rem",
                                    fontWeight: 600,
                                    color: isDark ? "#e2e8f0" : "#1e293b",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {location.streetAddress}
                                  {location.unitNumber ? `, ${location.unitNumber}` : ""}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.78rem",
                                    color: isDark ? "#64748b" : "#94a3b8",
                                    mt: 0.2,
                                  }}
                                >
                                  {addressLine}
                                  {isBilling && (
                                    <Box
                                      component="span"
                                      sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                                    >
                                      {" • Billing Address"}
                                    </Box>
                                  )}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Right: Edit text btn + delete icon */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleEditActionLocation(location, rowIndex)}
                                sx={{
                                  textTransform: "none",
                                  fontSize: "0.82rem",
                                  fontWeight: 600,
                                  color: isDark ? "#cbd5e1" : "#475569",
                                  minWidth: "auto",
                                  px: 1,
                                  py: 0.5,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: "#14b8a6",
                                  },
                                }}
                              >
                                Edit
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteActionLocation(location, rowIndex)}
                                sx={{
                                  color: "#ef4444",
                                  p: 0.5,
                                  "&:hover": {
                                    backgroundColor: "rgba(239,68,68,0.08)",
                                  },
                                }}
                              >
                                <DeleteOutlineOutlined fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        color: isDark ? "#64748b" : "#94a3b8",
                        mb: 3,
                      }}
                    >
                      No practice locations added yet.
                    </Typography>
                  )}
                </>
              );
            }}
          />

          {/* ── Providers ── */}
          {/* <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography sx={sectionTitleSx}>Providers *</Typography>
            <Button startIcon={<AddIcon />} size="small" sx={addBtnSx} onClick={clickShowProviderPopup}>
              Add Provider
            </Button>
          </Box>
          <Divider sx={dividerSx} />
          {hasPracticeProviders && (
            <Box pt={1}>
              <LegacyToolBar items={legacyItems} />
              <Table
                columns={columnsProvider}
                rows={formattedProvidersInfo}
                onRowClick={handleRowClick}
                actionComponent={(row: any, rowIndex: number) =>
                  actionComponentProviderPopup(row, rowIndex)
                }
                {...ProviderPopup}
                disablePagination={true}
                borderBox={true}
              />
            </Box>
          )}
          {!hasPracticeProviders && (
            <Typography
              sx={{ fontSize: "0.85rem", color: isDark ? "#64748b" : "#94a3b8", mb: 3 }}
            >
              No providers added yet.
            </Typography>
          )} */}
          <Controller
            name="practiceProviders"
            control={control}
            rules={{ required: "At least 1 provider is required." }}
            render={({ field, fieldState }) => {
              const hasPracticeProviders = getValues("practiceProviders")?.length > 0;
              return (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={sectionTitleSx}>Providers *</Typography>
                    <Button
                      startIcon={<AddIcon />}
                      size="small"
                      sx={addBtnSx}
                      onClick={clickShowProviderPopup}
                    >
                      Add Provider
                    </Button>
                  </Box>
                  <Divider sx={dividerSx} />

                  {fieldState.error && !hasPracticeProviders && (
                    <Typography
                      color="error"
                      variant="body2"
                      style={{ marginTop: "8px" }}
                    >
                      {fieldState.error.message}
                    </Typography>
                  )}

                  {hasPracticeProviders && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                      {formattedProvidersInfo.map((provider: any, rowIndex: number) => (
                        <Box
                          key={rowIndex}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2,
                            py: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                            backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                            transition: "border-color 0.15s",
                            "&:hover": {
                              borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <PersonOutline
                              sx={{
                                mt: 0.2,
                                fontSize: "1.2rem",
                                color: isDark ? "#475569" : "#94a3b8",
                                flexShrink: 0,
                              }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.88rem",
                                  fontWeight: 600,
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                  lineHeight: 1.4,
                                }}
                              >
                                {provider.providerName}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.78rem",
                                  color: isDark ? "#64748b" : "#94a3b8",
                                  mt: 0.2,
                                }}
                              >
                                {[
                                  provider.npi && `NPI: ${provider.npi}`,
                                  provider.ptan && `PTAN: ${provider.ptan}`,
                                ]
                                  .filter(Boolean)
                                  .join(" • ")}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => handleEditActionProvider(provider, rowIndex)}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                color: isDark ? "#cbd5e1" : "#475569",
                                minWidth: "auto",
                                px: 1,
                                py: 0.5,
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  color: "#14b8a6",
                                },
                              }}
                            >
                              Edit
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteActionProvider(provider, rowIndex)}
                              sx={{
                                color: "#ef4444",
                                p: 0.5,
                                "&:hover": {
                                  backgroundColor: "rgba(239,68,68,0.08)",
                                },
                              }}
                            >
                              <DeleteOutlineOutlined fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {!hasPracticeProviders && (
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        color: isDark ? "#64748b" : "#94a3b8",
                        mb: 3,
                      }}
                    >
                      No providers added yet.
                    </Typography>
                  )}
                </>
              );
            }}
          />

          {/* ── Account Payable Information ── */}
          <Typography sx={sectionTitleSx}>Account Payable Information</Typography>
          <Divider sx={dividerSx} />

          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} sm={12}>
              <Typography component="label" sx={labelSx}>AP Contact Name *</Typography>
              <CustomNametField control={control} context="AP" size="small" xs={12} md={4} usePlaceholder={true} isRequired={true} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" sx={labelSx}>AP Contact Phone *</Typography>
              <CustomPatternTextField control={control} name={"apPhone"} placeholder="(555) 555-5555"
                size="small"
                rules={{
                  required: "AP Contact Phone is required.",
                  pattern: {
                    value: /^(\+\d{1,2}\s)?\(\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                    message: "Invalid phone number.",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" sx={labelSx}>AP Contact Email *</Typography>
              <CustomTextField control={control} controllerName={"apEmail"} placeholder="ap@practice.com"
                size="small"
                rules={{
                  required: "AP Contact Email is required.",
                  pattern: {
                    value: /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|n\/a|N\/A)$/i,
                    message: "Invalid email.",
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* ── Biller Information ── */}
          <Typography sx={sectionTitleSx}>Biller Information</Typography>
          <Divider sx={{ ...dividerSx, mb: 2 }} />
          <Grid item sx={{ mb: 2 }}>
            <CustomRadioButton control={control} controllerName="billerSelection"
              radioSx={radioSx}
              radioLabelSx={radioLabelSx}
              options={[
                { value: "primary", label: "Same as Primary Contact" },
                { value: "accountPayable", label: "Same as Account Payable" },
                { value: "different", label: "Different Contact" },
              ]}
              rules={{ required: "Biller Information is required." }}
              onChange={(e) => {
                const value = e.target.value as "primary" | "accountPayable" | "different";
                setBillerType(value);
                setOfficeContactChecked(value === "primary");
                setAccountInformationChecked(value === "accountPayable");
              }}
            />
          </Grid>

          {billerType === "different" && (
            <Grid container spacing={2.5} mb={3}>
              <Grid item xs={12} sm={12}>
                <Typography component="label" sx={labelSx}>Biller Name *</Typography>
                <CustomNametField control={control} context="biller" name={"practiceRequestInfo"}
                  label="Biller Contact" xs={12} md={4} usePlaceholder={true} isRequired={true} size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography component="label" sx={labelSx}>Biller Phone *</Typography>
                <CustomPatternTextField control={control} placeholder="(555) 555-5555"
                  name={`practiceRequestInfo.${"billerContactPhone"}`}
                  size="small"
                  rules={{
                    required: "Biller Contact Phone is required.",
                    pattern: {
                      value: /^(\+\d{1,2}\s)?\(\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                      message: "Invalid phone number.",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography component="label" sx={labelSx}>Biller Email *</Typography>
                <CustomTextField control={control} controllerName={`practiceRequestInfo.${"billerContactEmail"}`}
                  placeholder="biller@practice.com"
                  size="small"
                  rules={{
                    required: "Biller Contact Email is required.",
                    pattern: {
                      value: /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|n\/a|N\/A)$/i,
                      message: "Invalid email.",
                    },
                  }}
                />
              </Grid>
            </Grid>
          )}

          {/* ── Authorized Signers ── */}
          {/* <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography sx={sectionTitleSx}>Authorized Signer(s) *</Typography>
            <Button startIcon={<AddIcon />} size="small" sx={addBtnSx} onClick={clickShowAuthorizedSignerPopup}>
              Add Authorized Signer
            </Button>
          </Box>
          <Divider sx={dividerSx} />
          {hasAuthorizedSigners && (
            <Box pt={1}>
              <LegacyToolBar items={legacyItems} />
              <Table
                columns={columnsSigner}
                rows={formatAuthorizedSigners(getValues("practiceRequestInfo.authorizedSigners")) || []}
                actionComponent={(row: any, rowIndex: number) => actionComponentAuthorizedSigner(row, rowIndex)}
                onRowClick={handleRowClick}
                disablePagination={true}
                borderBox={true}
              />
            </Box>
          )}
          {!hasPracticeLocations && (
            <Typography
              sx={{ fontSize: "0.85rem", color: isDark ? "#64748b" : "#94a3b8", mb: 3 }}
            >
              No authorized signers added yet.
            </Typography>
          )} */}

          <Controller
            name={`practiceRequestInfo.${"authorizedSigners"}`}
            control={control}
            rules={{
              required: "At least 1 Authorized Signer is required.",
            }}
            render={({ field, fieldState }) => {
              const hasAuthorizedSigners =
                getValues("practiceRequestInfo.authorizedSigners")?.length > 0;
              return (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={sectionTitleSx}>Authorized Signer(s) *</Typography>
                    <Button
                      startIcon={<AddIcon />}
                      size="small"
                      sx={addBtnSx}
                      onClick={clickShowAuthorizedSignerPopup}
                    >
                      Add Authorized Signer
                    </Button>
                  </Box>
                  <Divider sx={dividerSx} />

                  {fieldState.error && (
                    <Typography
                      color="error"
                      variant="body2"
                      style={{ marginTop: "8px" }}
                    >
                      {getErrorAuthorizedSignerMessage(field, fieldState, hasAuthorizedSigners)}
                    </Typography>
                  )}

                  {hasAuthorizedSigners && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                      {formatAuthorizedSigners(getValues("practiceRequestInfo.authorizedSigners")).map((signer: any, rowIndex: number) => (
                        <Box
                          key={rowIndex}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2,
                            py: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                            backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                            transition: "border-color 0.15s",
                            "&:hover": {
                              borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <VerifiedUserOutlined
                              sx={{
                                mt: 0.2,
                                fontSize: "1.2rem",
                                color: isDark ? "#475569" : "#94a3b8",
                                flexShrink: 0,
                              }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.88rem",
                                  fontWeight: 600,
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                  lineHeight: 1.4,
                                }}
                              >
                                {signer.fullTitleName}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.78rem",
                                  color: isDark ? "#64748b" : "#94a3b8",
                                  mt: 0.2,
                                }}
                              >
                                {[
                                  signer.email,
                                  signer.position && `Position: ${signer.position}`,
                                  signer.isOwner && `Ownership: ${signer.isOwner}`,
                                ]
                                  .filter(Boolean)
                                  .join(" • ")}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => handleSignerChange(signer, rowIndex)}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                color: isDark ? "#cbd5e1" : "#475569",
                                minWidth: "auto",
                                px: 1,
                                py: 0.5,
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  color: "#14b8a6",
                                },
                              }}
                            >
                              Edit
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteAuthorizedSigner(signer, rowIndex)}
                              sx={{
                                color: "#ef4444",
                                p: 0.5,
                                "&:hover": {
                                  backgroundColor: "rgba(239,68,68,0.08)",
                                },
                              }}
                            >
                              <DeleteOutlineOutlined fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {!hasAuthorizedSigners && (
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        color: isDark ? "#64748b" : "#94a3b8",
                        mb: 3,
                      }}
                    >
                      No authorized signers added yet.
                    </Typography>
                  )}
                </>
              );
            }}
          />

          {/* ── Specializations & Services ── */}
          <Typography sx={sectionTitleSx}>Specializations & Services</Typography>
          <Divider sx={dividerSx} />

          <Typography component="label" sx={labelSx}>
            Type of Specialization(s) *
          </Typography>
          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} sm={6}>
              <CustomDropdownField
                control={control}
                name={"specialization"}
                options={
                  practiceSpecializationList
                    ? practiceSpecializationList.map((option) => ({
                      value: option.keyName,
                      label: option.displayValue,
                    }))
                    : []
                }
                placeholderText="Type of Specialization(s)"
                rules={{ required: "Type of Specialization(s) is required." }}
                size="small"
                sortOptions={false}
              />
            </Grid>
            <Grid item xs={12} sm={6}></Grid>
            {watch("specialization") !== undefined && watch("specialization") !== null && watch("specialization") !== "" && watch("specialization") !== "Surgical Only" && (<>
              <Grid item xs={12} sm={6}>
                <Typography component="label" sx={labelSx}>
                  Wound Care Focus *
                </Typography>
                <CustomDropdownField
                  control={control}
                  name={`practiceRequestInfo.${"woundCareFocus"}`}
                  options={
                    woundCareOptions
                      ? woundCareOptions.map((option) => ({
                        value: option.keyName,
                        label: option.displayValue,
                      }))
                      : []
                  }
                  placeholderText="Wound Care Focus"
                  size="small"
                  rules={{ required: "Wound Care Focus is required." }}
                  sortOptions={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography component="label" sx={labelSx}>
                  Average Wounds Treated Per Month *
                </Typography>
                <CustomNumberField control={control} controllerName={watch("practiceRequestInfo.woundCareFocus") === "Not Available" ? "" : `practiceRequestInfo.${"averageWoundsTreatedPerMonth"}`}
                  placeholder="Average Wounds Treated Per Month" isDisabled={watch("practiceRequestInfo.woundCareFocus") === "Not Available"} isEmpty={watch("practiceRequestInfo.woundCareFocus") === "Not Available"}
                  rules={
                    watch("practiceRequestInfo.woundCareFocus") === "Not Available" ? {} :
                      {
                        required: "Average Wounds Treated Per Month is required.",
                        pattern: {
                          value: /^[0-9]+$/,
                          message: "Please enter a valid number.",
                        },
                      }}
                  size="small"
                />
              </Grid>
              <Grid item md={4}></Grid>
            </>)}

          </Grid>

          <Typography component="label" sx={labelSx}>
            What areas of care would you like to focus on? *
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1, mb: 3 }}>
            <Box
              onClick={() => {
                const allKeys = productCategoryOptions.map((o) => o.value);
                setValue(
                  "practiceRequestInfo.interestedProductCategory",
                  allCareAreasSelected ? [] : allKeys,
                  { shouldValidate: true }
                );
                setTimeout(() => handleProductCategoryChange(), 0);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                alignSelf: "flex-start",
                gap: 1,
                px: 1.5,
                py: 0.8,
                borderRadius: 1.5,
                cursor: "pointer",
                userSelect: "none",
                border: `1.5px solid ${
                  allCareAreasSelected
                    ? "rgba(20,184,166,0.5)"
                    : isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)"
                }`,
                backgroundColor: allCareAreasSelected
                  ? isDark
                    ? "rgba(20,184,166,0.06)"
                    : "rgba(20,184,166,0.04)"
                  : isDark
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.015)",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: allCareAreasSelected
                    ? "#14b8a6"
                    : isDark
                      ? "rgba(255,255,255,0.16)"
                      : "rgba(0,0,0,0.16)",
                },
              }}
            >
              <Checkbox
                checked={allCareAreasSelected}
                disableRipple
                size="small"
                sx={{
                  color: isDark ? "#475569" : "#94a3b8",
                  "&.Mui-checked": { color: "#14b8a6" },
                  p: 0,
                }}
                tabIndex={-1}
              />
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  color: isDark ? "#e2e8f0" : "#1e293b",
                  lineHeight: 1.3,
                }}
              >
                Select All
              </Typography>
            </Box>
            
            <CustomCheckboxGroup
              control={control}
              controllerName="practiceRequestInfo.interestedProductCategory"
              options={productCategoryOptions}
              rules={{ required: "At least one area of care must be selected." }}
              onChange={handleProductCategoryChange}
              checkboxSx={checkboxSx}
              radioLabelSx={radioLabelSx}
              cardMode
            />
          </Box>
          
          {(watchedCategories || []).includes(PRODUCT_CATEGORY.CONSERVATIVE) && (
            <Box mb={3}>
              <Typography component="label" sx={labelSx}>
                Date of Service Preference *
              </Typography>
              <CustomRadioButton
                control={control}
                radioSx={radioSx}
                radioLabelSx={radioLabelSx}
                controllerName="dosPreference"
                options={preferenceOptions}
                rules={{
                  required: "Date of Service Preference is required.",
                }}
              />
            </Box>
          )}

          <Typography component="label" sx={labelSx}>
            Place(s) of Service *
          </Typography>
          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} sm={12}>
              <CustomCheckboxGroup control={control} controllerName={`practiceRequestInfo.${"placesOfService"}`}
                options={
                  placeOfService
                    ? placeOfService.map((option) => ({
                      value: option.keyName,
                      label: option.displayValue,
                    }))
                    : []
                }
                rules={{
                  required: "Place(s) of Service is required.",
                }}
                checkboxSx={checkboxSx}
                radioLabelSx={radioLabelSx}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2.5} mb={1}>
            <Grid item xs={12} sm={12}>
              <Typography component="label" sx={labelSx}>
                Representative or Distributor Name {noRepChecked ? "" : "*"}
              </Typography>
              <CustomTextField
                control={control}
                controllerName={`practiceRequestInfo.${"repOrDistributorName"}`}
                placeholder="Enter the name of the representative or distributor"
                size="small"
                isDisabled={noRepChecked}
                rules={
                  noRepChecked
                    ? {}
                    : { required: "Name of the Representative or Distributor is required." }
                }
              />
            </Grid>
          </Grid>
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={noRepChecked}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setNoRepChecked(checked);
                    if (checked) {
                      unregister("practiceRequestInfo.repOrDistributorName");
                      setValue("practiceRequestInfo.repOrDistributorName", "No Representative or Distributor provided.");
                      clearErrors("practiceRequestInfo.repOrDistributorName");
                    }
                  }}
                  size="small"
                  sx={{
                    color: isDark ? "#64748b" : "#94a3b8",
                    "&.Mui-checked": { color: "#14b8a6" },
                    padding: "4px 8px",
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    color: isDark ? "#cbd5e1" : "#475569",
                  }}
                >
                  I don't have a representative or distributor
                </Typography>
              }
            />
          </Box>

          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} sm={12}>
              <Typography component="label" sx={labelSx}>
                Additional Notes
              </Typography>
              <CustomTextField multiline control={control} controllerName={`practiceRequestInfo.${"anyQuestions"}`}
                rows={4} maxRows={4}
                placeholder="Please add any notes or specific requests that will help us serve you properly."
              />
            </Grid>
          </Grid>

        </DialogContent>

        {/* ── Footer ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"
              }`,
          }}
        >
          <Button
            variant="contained"
            type="submit"
            startIcon={
              <CheckCircleOutline sx={{ fontSize: "1rem !important" }} />
            }
            sx={primaryBtnSx}
          >
            Submit Registration
          </Button>
          <Button
            variant="outlined"
            startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
            onClick={onClose}
            sx={cancelBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </form>
      <PracticeLocationPopup
        onClose={clickClosePracticesPopup}
        open={showPracticesPopup}
        addPractice={addPractice}
        practiceLocations={getValues("practiceLocations") || []}
        billingAddressPanel={showBillingAddressOption}
        practiceLocationObject={locationItemObject}
      />
      <ProviderPopup
        onClose={clickCloseProviderPopup}
        open={showProviderPopup}
        addProvider={addProvider}
        onChange={handleProviderChange}
        practiceProviderObject={providerItemObject}
        billingType={getValues("billingType")}
      />
      {showAuthorizedSignerPopup &&
        <AuthorizedSignerPopup
          onClose={clickCloseAuthorizedSignerPopup}
          open={showAuthorizedSignerPopup}
          addAuthorizedSigner={addSigner}
          authorizedSignerData={authorizedSignerData}
        />
      }
    </Dialog>
  );
};

export default CompletePracticeRegistrationModal;